// frontend/src/pages/portfolio/components/DashboardMockup.tsx
import React from "react";
import { BarChart3, Clock, Users, TrendingUp } from "lucide-react";

const DashboardMockup: React.FC = () => {
  return (
    <div className="relative" aria-hidden>
      {/* Glow */}
      <div className="absolute -inset-6 rounded-[2.25rem] bg-gradient-to-br from-primary-200/50 via-transparent to-primary-400/25 blur-3xl motion-reduce:hidden" />

      {/* Browser frame */}
      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-modal ring-1 ring-primary-900/[0.05]">
        {/* Browser chrome */}
        <div className="flex items-center gap-2 border-b border-gray-100 bg-slate-50 px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-error-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-warning-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-success-300" />
          <div className="ml-3 hidden h-5 flex-1 max-w-xs rounded-md bg-white ring-1 ring-gray-200 sm:block" />
        </div>

        {/* Dashboard body */}
        <div className="flex h-full">
          {/* Sidebar */}
          <aside className="hidden w-40 shrink-0 border-r border-gray-100 bg-slate-50/60 p-3 sm:block">
            <div className="mb-4 h-6 w-24 rounded bg-primary-900/90" />
            <div className="space-y-1.5">
              {["Dashboard", "Calendar", "Users", "Billing"].map((label, i) => (
                <div
                  key={label}
                  className={`h-7 rounded-md px-2 text-[10px] font-medium leading-7 ${
                    i === 0
                      ? "bg-primary-100 text-primary-900"
                      : "text-primary-700/70"
                  }`}
                >
                  {label}
                </div>
              ))}
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 p-4 sm:p-5">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700/70">
                  This week
                </div>
                <div className="text-sm font-semibold text-primary-950 sm:text-base">
                  Team overview
                </div>
              </div>
              <div className="hidden h-7 w-24 rounded-md bg-primary-900 sm:block" />
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { icon: Users, label: "Active", value: "24" },
                { icon: Clock, label: "Hours", value: "182" },
                { icon: TrendingUp, label: "Rate", value: "94%" },
              ].map(({ icon: Icon, label, value }) => (
                <div
                  key={label}
                  className="rounded-lg border border-gray-100 bg-white p-2 sm:p-3"
                >
                  <Icon className="h-3.5 w-3.5 text-primary-600" aria-hidden />
                  <div className="mt-1 font-display text-base font-bold tabular-nums text-primary-950 sm:text-lg">
                    {value}
                  </div>
                  <div className="text-[9px] font-medium uppercase tracking-wider text-primary-700/60 sm:text-[10px]">
                    {label}
                  </div>
                </div>
              ))}
            </div>

            {/* Faux chart */}
            <div className="mt-4 rounded-lg border border-gray-100 bg-white p-3 sm:mt-5">
              <div className="mb-2 flex items-center gap-2">
                <BarChart3 className="h-3.5 w-3.5 text-primary-600" aria-hidden />
                <span className="text-[10px] font-semibold text-primary-900">
                  Activity
                </span>
              </div>
              <div className="flex h-16 items-end gap-1 sm:h-20">
                {[40, 70, 55, 85, 60, 90, 75].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t bg-gradient-to-t from-primary-300 to-primary-500"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating clock-in card (hidden on small screens) */}
      <div className="absolute -bottom-6 -left-4 hidden w-56 rounded-xl border border-gray-200 bg-white p-4 shadow-elevated ring-1 ring-primary-900/[0.05] sm:block">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-success-50 text-success-700 ring-1 ring-success-200">
            <Clock className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-primary-950">Clocked in</div>
            <div className="text-[11px] text-gray-500 tabular-nums">09:14 · today</div>
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full w-3/4 rounded-full bg-gradient-to-r from-primary-400 to-primary-600" />
        </div>
      </div>
    </div>
  );
};

export default DashboardMockup;
